import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Camera } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";

const ArrivalPhotosPage = () => {
  const { id } = useParams();
  const [inspection, setInspection] = useState(null);
  const [photos, setPhotos] = useState({ FL: null, FR: null, RL: null, RR: null });
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const corners = [
    { key: "FL", label: "Front Left" },
    { key: "FR", label: "Front Right" },
    { key: "RL", label: "Rear Left" },
    { key: "RR", label: "Rear Right" }
  ];

  useEffect(() => {
    // First check if arrival photos already exist at job level (from Diagnostic VI)
    axios.get(`${API}/jobs/${id}/arrival-photos`).then(photosRes => {
      const existingPhotos = { FL: null, FR: null, RL: null, RR: null };
      photosRes.data.forEach(p => {
        existingPhotos[p.corner] = p.photo_url;
      });
      setPhotos(existingPhotos);
      
      // If all 4 photos already exist (from Diagnostic VI), skip to sections
      const photoCount = photosRes.data?.length || 0;
      if (photoCount >= 4) {
        navigate(`/app/tech/job/${id}/sections`);
        return;
      }
    }).catch(() => {
      // No photos yet, that's fine
    });
    
    // Also load inspection data for context
    axios.get(`${API}/jobs/${id}/inspection`).then(res => {
      if (!res.data) {
        navigate(`/app/tech/job/${id}/start`);
        return;
      }
      setInspection(res.data);
    }).finally(() => setLoading(false));
  }, [id, navigate]);

  const handleCapture = async (corner, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [corner]: true }));
    
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        // Use job-level endpoint so photos are shared between Diagnostic VI and VI
        await axios.post(`${API}/jobs/${id}/arrival-photos`, {
          corner,
          photo_data: base64
        });
        setPhotos(prev => ({ ...prev, [corner]: reader.result }));
      } catch (err) {
        alert("Failed to upload photo");
      } finally {
        setUploading(prev => ({ ...prev, [corner]: false }));
      }
    };
    reader.readAsDataURL(file);
  };

  const allPhotosUploaded = Object.values(photos).every(p => p !== null);

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="p-4 max-w-2xl mx-auto">
        <h2 className="text-xl font-semibold mb-2" data-testid="arrival-photos-title">Arrival Photos</h2>
        <p className="text-sm text-gray-600 mb-4">Capture photos of all 4 corners of the vehicle</p>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          {corners.map(({ key, label }) => (
            <Card key={key} data-testid={`photo-card-${key}`}>
              <CardContent className="p-4">
                <Label className="font-semibold">{label}</Label>
                <div className="mt-2 h-32 bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                  {photos[key] ? (
                    <img src={photos[key]} alt={label} className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div className="mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    id={`photo-${key}`}
                    onChange={(e) => handleCapture(key, e.target.files[0])}
                    disabled={uploading[key]}
                    data-testid={`photo-input-${key}`}
                  />
                  <Button
                    variant={photos[key] ? "outline" : "default"}
                    size="sm"
                    className="w-full"
                    onClick={() => document.getElementById(`photo-${key}`).click()}
                    disabled={uploading[key]}
                    data-testid={`capture-btn-${key}`}
                  >
                    {uploading[key] ? "Uploading..." : photos[key] ? "Retake" : "Capture"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Button
          className="w-full"
          disabled={!allPhotosUploaded}
          onClick={() => navigate(`/app/tech/job/${id}/sections`)}
          data-testid="proceed-to-sections-btn"
        >
          {allPhotosUploaded ? "Continue to Inspection" : `Upload all 4 photos (${Object.values(photos).filter(p => p).length}/4)`}
        </Button>
      </div>
    </div>
  );
};

export default ArrivalPhotosPage;
